import { ProtectedServerPlaceEvent } from 'gleblu-socket-api';
import moment from 'moment-timezone';
import { Action } from 'redux-act';
import { put, takeEvery } from 'redux-saga/effects';

import { fromDto as fromMergedTableDto, toDto as toMergedTableDto } from '../../../models/adapters/MergedTableAdapter';
import { fromDto as reservationFromDto, toDto as toReservationDto } from '../../../models/adapters/ReservationAdapter';
import { toDto as toReservationSeatingDto } from '../../../models/adapters/ReservationSeatingAdapter';
import { SeatedReservation } from '../../../models/interfaces/reservation/Reservation';
import { successMap } from '../../../models/interfaces/reservation/ReservationAcceptState';
import ReservationRejectReason, { failureMap } from '../../../models/interfaces/reservation/ReservationRejectReason';
import {
  reservationResolved,
  reservationResolveFailed,
  ReservationResolveFailedPayload,
  resolveReservation,
  ResolveReservationPayload,
} from '../../actions/reservation/resolveReservation';
import getAuthenticationToken from '../getAuthenticationToken';
import getProtectedResponse from '../getProtectedResponse';

function* resolveReservationSaga({ payload }: Action<ResolveReservationPayload>) {
  const { pendingReservation, seating, mergedTable } = payload;
  const reservationDto = toReservationDto(pendingReservation);
  const reservationSeatingDto = seating ? toReservationSeatingDto(seating) : undefined;
  const mergedTableDto = mergedTable ? toMergedTableDto(mergedTable) : undefined;
  const token = yield getAuthenticationToken();
  const socketPayload = { reservationDto, reservationSeatingDto, mergedTableDto };
  const response = yield getProtectedResponse(ProtectedServerPlaceEvent.resolveReservation, token, socketPayload);
  const { error, reservationConfirmation } = response;
  const timestamp = moment();
  const unknownRejectPayload: ReservationResolveFailedPayload = {
    timestamp,
    reservationUuid: pendingReservation.uuid,
    rejectReason: ReservationRejectReason.Unknown,
  };

  if (error || !reservationConfirmation) {
    yield put(reservationResolveFailed(unknownRejectPayload));
  } else if (successMap.has(reservationConfirmation)) {
    const { acceptedReservation: acceptedReservationDto, createdMergedTable: createdMergedTableDto } = response;
    const acceptedReservation = reservationFromDto(acceptedReservationDto) as SeatedReservation;
    const createdMergedTable = createdMergedTableDto ? fromMergedTableDto(createdMergedTableDto) : null;
    const createdPayload = {
      timestamp,
      acceptState: successMap.get(reservationConfirmation),
      reservation: acceptedReservation,
      reservationUuid: acceptedReservation.uuid,
      createdMergedTable,
    };

    yield put(reservationResolved(createdPayload));
  } else if (failureMap.has(reservationConfirmation)) {
    const failedPayload = {
      timestamp,
      rejectReason: failureMap.get(reservationConfirmation),
      reservationUuid: pendingReservation.uuid,
      placeUuid: reservationDto.placeUuid,
    };

    yield put(reservationResolveFailed(failedPayload));
  } else {
    yield put(reservationResolveFailed(unknownRejectPayload));
  }
}

export default function* watchResolveReservationSaga() {
  yield takeEvery(resolveReservation, resolveReservationSaga);
}
